<template>
  <div class="knowledge-resource-admin">
    <div class="course-filter-bar">
      <label>选择课程 <span class="required-mark">*</span></label>
      <select v-model="selectedCourseId" @change="onCourseChange">
        <option :value="null">请选择课程</option>
        <option v-for="course in courses" :key="course.kcId" :value="course.kcId">
          {{ course.name }}（{{ course.category }}）
        </option>
      </select>
      <span v-if="selectedCourseId" class="course-filter-hint">仅显示该课程下知识点绑定的学习资源</span>
    </div>

    <div v-if="!creatorMode && unboundTotal > 0" class="legacy-panel">
      <h4 class="legacy-panel__title">未归属资源（{{ unboundTotal }}）</h4>
      <p class="legacy-panel__hint">历史学习资源尚未关联课程。请先在上方选择目标课程，再点击「关联到所选课程」（可在弹窗中选择本课程知识点）。</p>
      <p v-if="selectedCourseId && (knowledgePoints || []).length === 0" class="legacy-panel__warn">
        当前课程暂无知识点，请先在
        <router-link :to="{ path: '/admin/knowledge/points', query: { kcId: selectedCourseId } }">知识点管理</router-link>
        中将历史知识点关联到本课程。
      </p>
      <table class="resource-table">
        <thead>
          <tr>
            <th>ID</th>
            <th>名称</th>
            <th>类型</th>
            <th>内容摘要</th>
            <th>目标知识点</th>
            <th>操作</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="resource in unboundResources" :key="'u-' + resource.krId">
            <td>{{ resource.krId }}</td>
            <td>{{ resource.name || '-' }}</td>
            <td>{{ getResourceTypeText(resource.type) }}</td>
            <td>{{ truncateContent(resource.content) }}</td>
            <td>
              <select
                class="legacy-kp-select"
                :value="unboundTargetKp[resource.krId] || ''"
                :disabled="!selectedCourseId || (knowledgePoints || []).length === 0"
                @change="setUnboundTargetKp(resource.krId, $event.target.value)"
              >
                <option value="">请选择知识点</option>
                <option v-for="kp in (knowledgePoints || [])" :key="kp.kpId" :value="kp.kpId">
                  {{ kp.name }}
                </option>
              </select>
            </td>
            <td>
              <button
                class="btn-assign"
                :disabled="!selectedCourseId"
                @click="assignResourceToCourse(resource)"
              >
                关联到所选课程
              </button>
            </td>
          </tr>
        </tbody>
      </table>
      <AdminPagination
        :current-page="unboundPage"
        :page-size="unboundPageSize"
        :page-sizes="[20, 50, 100]"
        :total="unboundTotal"
        @update:current-page="changeUnboundPage"
        @size-change="handleUnboundPageSizeChange"
      />
    </div>

    <div v-if="!selectedCourseId" class="empty-course-hint">
      <p class="empty-course-hint__title">请先选择课程</p>
      <p class="empty-course-hint__desc">在上方下拉框中选择一门课程后，方可创建、绑定或维护学习资源。</p>
    </div>

    <template v-else>
      <div class="admin-toolbar admin-toolbar--end">
        <span class="selected-course-label">当前课程：{{ selectedCourseName }}</span>
        <button class="btn-primary" @click="openCreateModal">创建新资源</button>
      </div>

      <div class="resource-list">
      <div class="search-bar">
        <input 
          type="text" 
          v-model="searchKeyword" 
          placeholder="查找名称、内容、URL或iframe..."
          @input="handleSearch"
        >
        <select v-model="selectedKpId" @change="handleKpFilter">
          <option value="">本课程全部知识点</option>
          <option v-for="kp in (knowledgePoints || [])" :key="kp.kpId" :value="kp.kpId">
            {{ kp.name }}
          </option>
        </select>
      </div>
      
      <table class="resource-table">
        <thead>
          <tr>
            <th>ID</th>
            <th>名称</th>
            <th>内容</th>
            <th>URL</th>
            <th>iframe</th>
            <th>类型</th>
            <th>难度</th>
            <th>绑定知识点</th>
            <th>创建时间</th>
            <th>操作</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="resource in filteredResources" :key="resource.krId">
            <td>{{ resource.krId }}</td>
            <td>{{ resource.name || '-' }}</td>
            <td>{{ truncateContent(resource.content) }}</td>
            <td>
              <span v-if="resource.url">
                <a :href="resource.url" target="_blank" class="table-url" :title="resource.url">
                  {{ truncateUrl(resource.url) }}
                </a>
              </span>
              <span v-else class="no-url">-</span>
            </td>
            <td>
              <span v-if="resource.iframe" class="iframe-preview" :title="resource.iframe">
                {{ truncateIframe(resource.iframe) }}
              </span>
              <span v-else class="no-iframe">-</span>
            </td>
            <td>{{ getResourceTypeText(resource.type) }}</td>
            <td>{{ getDifficultyText(resource.difficulty) }}</td>
            <td>{{ getBoundKnowledgePoints(resource.krId) }}</td>
            <td>{{ formatTime(resource.createdAt) }}</td>
            <td>
              <button class="btn-view" @click="viewResource(resource)">查看</button>
              <button class="btn-edit" @click="editResource(resource)">编辑</button>
              <button class="btn-bind" @click="showBindModals(resource)">绑定</button>
              <button class="btn-delete" @click="deleteResource(resource)">删除</button>
            </td>
          </tr>
        </tbody>
      </table>

      <AdminPagination
        :current-page="currentPage"
        :page-size="pageSize"
        :page-sizes="[10, 20, 50, 100]"
        :total="total"
        @update:current-page="changePage"
        @size-change="handlePageSizeChange"
      />
      </div>
    </template>

    <!-- Create/Edit Modal -->
    <div class="modal" v-if="showCreateModal || showEditModal">
      <div class="modal-content">
        <h3>{{ showEditModal ? '编辑资源' : '创建资源' }}</h3>
        <form @submit.prevent="handleSubmit">
          <div class="form-group">
            <label>名称 *</label>
            <input v-model="formData.name" type="text" placeholder="资源名称" required>
          </div>
          
          <div class="form-group">
            <label>内容</label>
            <textarea v-model="formData.content" rows="6" placeholder="选填，为空时学习页不展示正文"></textarea>
          </div>
          
          <div class="form-group">
            <label>URL</label>
            <input v-model="formData.url" type="url" placeholder="https://example.com">
          </div>
          
          <div class="form-group">
            <label>iframe</label>
            <textarea v-model="formData.iframe" rows="4" placeholder="<iframe src='...'></iframe>"></textarea>
          </div>
          
          <div class="form-group">
            <label>类型 *</label>
            <select v-model="formData.type" required>
              <option value="text">文本</option>
              <option value="video">视频</option>
              <option value="document">文档</option>
              <option value="audio">音频</option>
              <option value="lecture">讲义</option>
              <option value="exercise">习题</option>
            </select>
          </div>
          
          <div class="form-group">
            <label>难度 *</label>
            <select v-model="formData.difficulty" required>
              <option value="1">初级</option>
              <option value="2">中级</option>
              <option value="3">高级</option>
            </select>
          </div>

          <div class="form-actions">
            <button type="submit" class="btn-primary">
              {{ showEditModal ? '更新' : '创建' }}
            </button>
            <button type="button" class="btn-secondary" @click="closeModal">取消</button>
          </div>
        </form>
      </div>
    </div>

    <!-- View Modal -->
    <div class="modal" v-if="showViewModal">
      <div class="modal-content">
        <h3>资源详情</h3>
        <div class="resource-details">
          <div class="detail-item">
            <label>ID:</label>
            <span>{{ selectedResource.krId }}</span>
          </div>
          <div class="detail-item">
            <label>名称:</label>
            <span>{{ selectedResource.name || '未设置' }}</span>
          </div>
          <div class="detail-item">
            <label>类型:</label>
            <span>{{ getResourceTypeText(selectedResource.type) }}</span>
          </div>
          <div class="detail-item">
            <label>难度:</label>
            <span>{{ getDifficultyText(selectedResource.difficulty) }}</span>
          </div>
          <div class="detail-item">
            <label>内容:</label>
            <div v-if="selectedResource.content && String(selectedResource.content).trim()" class="content-display" v-html="selectedResource.content"></div>
            <span v-else class="no-url">无</span>
          </div>
          <div class="detail-item">
            <label>URL:</label>
            <template v-if="selectedResource.useVedioPlayer">
              <video-player :options="videoOptions" :is-audio="selectedResource.isAudio" />
            </template>
            <span v-else-if="selectedResource.url">
              <a :href="selectedResource.url" target="_blank" class="resource-url">{{ selectedResource.url }}</a>
            </span>
            <span v-else class="no-url">无</span>
          </div>
          <div class="detail-item">
            <label>iframe:</label>
            <div v-if="selectedResource.iframe" class="iframe-display">
              <div class="iframe-code">{{ selectedResource.iframe }}</div>
              <div class="iframe-preview-container">
                <div class="iframe-preview-label">预览:</div>
                <div class="iframe-preview-content" v-html="selectedResource.iframe"></div>
              </div>
            </div>
            <span v-else class="no-iframe">无</span>
          </div>
          <div class="detail-item">
            <label>绑定知识点:</label>
            <div class="bound-kps">
              <span v-for="kp in getBoundKnowledgePointsList(selectedResource.krId)" :key="kp.kpId" class="kp-tag">
                {{ kp.name }}
              </span>
            </div>
          </div>
          <div class="detail-item">
            <label>创建时间:</label>
            <span>{{ formatTime(selectedResource.createdAt) }}</span>
          </div>
        </div>
        <div class="modal-actions">
          <button class="btn-secondary" @click="showViewModal = false">关闭</button>
        </div>
      </div>
    </div>

    <!-- Bind Modal -->
    <div class="modal" v-if="showBindModal">
      <div class="modal-content">
        <h3>绑定知识点</h3>
        <div class="bind-info">
          <p>为资源 "{{ selectedResource?.content?.substring(0, 50) }}..." 绑定知识点</p>
        </div>
        
        <div class="form-group">
          <label>选择知识点</label>
          <div class="multi-select-container">
            <div class="selected-items">
              <span 
                v-for="kp in selectedKnowledgePoints" 
                :key="kp.kpId"
                class="selected-item"
              >
                {{ kp.name }}
                <button type="button" @click="removeKnowledgePoint(kp)" class="remove-btn">×</button>
              </span>
            </div>
            <div class="select-dropdown">
              <input 
                type="text" 
                v-model="kpSearch" 
                placeholder="查找知识点..."
                @input="filterKnowledgePoints"
                @focus="showKpDropdown = true"
              >
              <div v-if="showKpDropdown && filteredKnowledgePoints.length > 0" class="dropdown-list">
                <div 
                  v-for="kp in filteredKnowledgePoints" 
                  :key="kp.kpId"
                  class="dropdown-item"
                  @click="addKnowledgePoint(kp)"
                >
                  {{ kp.name }} ({{ kp.category }})
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="form-actions">
          <button class="btn-primary" @click="handleBind">绑定</button>
          <button class="btn-secondary" @click="showBindModal = false">取消</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import VideoPlayer from '@/components/VideoPlayer.vue';
import AdminPagination from '@/components/AdminPagination.vue'

export default {
  name: 'KnowledgeResourceAdmin',
  components: {
    VideoPlayer,
    AdminPagination
  },
  props: {
    creatorMode: { type: Boolean, default: false },
    initialKcId: { type: [Number, String], default: null }
  },
  data() {
    return {
      courses: [],
      selectedCourseId: null,
      resources: [],
      knowledgePoints: [],
      kpRelations: [], // 知识点与资源的关系
      searchKeyword: '',
      selectedKpId: '',
      currentPage: 1,
      pageSize: 20,
      total: 0,
      totalPages: 1,
      
      // Modal states
      showCreateModal: false,
      showEditModal: false,
      showViewModal: false,
      showBindModal: false,
      
      // Form data
      formData: {
        name: '',
        content: '',
        url: '',
        iframe: '',
        type: 'text',
        difficulty: 1
      },
      
      // Selected items
      selectedResource: null,
      selectedKnowledgePoints: [],
      
      // Search and filter
      kpSearch: '',
      showKpDropdown: false,
      filteredKnowledgePoints: [],
      videoOptions: {},

      unboundResources: [],
      unboundTotal: 0,
      unboundPage: 1,
      unboundPageSize: 20,
      unboundTargetKp: {}
    }
  },
  computed: {
    selectedCourseName() {
      const course = this.courses.find(c => c.kcId === this.selectedCourseId)
      return course ? course.name : ''
    },
    filteredResources() {
      return this.resources
    }
  },
  created() {
    this.fetchCourses()
    this.fetchUnboundResources()
  },
  methods: {
    async fetchCourses() {
      try {
        const token = localStorage.getItem('jwt_token')
        if (this.creatorMode) {
          const response = await fetch('/api/v1/creator/knowledge/courses/list', {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({ page: 1, pageSize: 1000, keyword: '' })
          })
          const data = await response.json()
          if (data.code === 200) {
            this.courses = data.data?.list || []
          }
        } else {
          const response = await fetch('/api/v1/knowledge/courses/list', {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({ page: 1, pageSize: 1000, keyword: '' })
          })
          const data = await response.json()
          if (data.code === 200) {
            this.courses = data.data.list || []
          }
        }
        this.applyInitialCourse()
      } catch (error) {
        console.error('Failed to fetch courses:', error)
      }
    },
    applyInitialCourse() {
      if (!this.initialKcId || this.selectedCourseId) return
      const kcId = Number(this.initialKcId)
      if (this.courses.some(c => c.kcId === kcId)) {
        this.selectedCourseId = kcId
        this.onCourseChange()
      }
    },
    async fetchUnboundResources() {
      if (this.creatorMode) return
      try {
        const token = localStorage.getItem('jwt_token')
        const response = await fetch('/api/v1/knowledge/resources/list', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            unboundOnly: true,
            page: this.unboundPage,
            pageSize: this.unboundPageSize,
            keyword: ''
          })
        })
        const data = await response.json()
        if (data.code === 200) {
          this.unboundResources = data.data?.list || []
          this.unboundTotal = data.data?.total || 0
        } else if (data.code === 401) {
          this.$message.error(data.message || '没有权限查看未归属资源')
        }
      } catch (error) {
        console.error('Failed to fetch unbound resources:', error)
      }
    },
    setUnboundTargetKp(krId, kpId) {
      const next = { ...this.unboundTargetKp }
      if (kpId) {
        next[krId] = Number(kpId)
      } else {
        delete next[krId]
      }
      this.unboundTargetKp = next
    },
    async assignResourceToCourse(resource) {
      if (!this.selectedCourseId) {
        this.$message.warning('请先在上方选择目标课程')
        return
      }
      const kps = this.knowledgePoints || []
      if (kps.length === 0) {
        this.$message.warning('当前课程暂无知识点，请先在知识点管理中将历史知识点关联到本课程')
        return
      }
      let kpId = this.unboundTargetKp[resource.krId]
      if (!kpId) {
        if (kps.length === 0) {
          await this.fetchKnowledgePoints()
        }
        if ((this.knowledgePoints || []).length === 0) {
          this.$message.warning('当前课程暂无知识点，请先在知识点管理中将历史知识点关联到本课程')
          return
        }
        this.showBindModals(resource)
        return
      }
      const kp = kps.find(item => item.kpId === kpId)
      const label = resource.name || `资源#${resource.krId}`
      const kpName = kp ? kp.name : `知识点#${kpId}`
      if (!confirm(`将「${label}」关联到课程「${this.selectedCourseName}」的知识点「${kpName}」？`)) return
      try {
        const token = localStorage.getItem('jwt_token')
        const response = await fetch('/api/v1/knowledge/resources/bind', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ kpId, krId: resource.krId })
        })
        const data = await response.json()
        if (data.code === 200) {
          this.$message.success('已关联到课程')
          const next = { ...this.unboundTargetKp }
          delete next[resource.krId]
          this.unboundTargetKp = next
          this.fetchUnboundResources()
          this.fetchKpRelations()
          if (this.selectedCourseId) {
            this.fetchResources()
          }
        } else {
          this.$message.error(data.message || '关联失败')
        }
      } catch (error) {
        console.error('Failed to assign resource:', error)
        this.$message.error('关联失败')
      }
    },
    changeUnboundPage(page) {
      this.unboundPage = page
      this.fetchUnboundResources()
    },
    handleUnboundPageSizeChange(size) {
      this.unboundPageSize = size
      this.unboundPage = 1
      this.fetchUnboundResources()
    },
    onCourseChange() {
      this.unboundTargetKp = {}
      this.selectedKpId = ''
      this.currentPage = 1
      this.searchKeyword = ''
      this.resources = []
      this.knowledgePoints = []
      this.total = 0
      if (!this.selectedCourseId) return
      const kcId = Number(this.selectedCourseId)
      if (kcId > 0) {
        this.selectedCourseId = kcId
      }
      this.fetchKnowledgePoints()
      this.fetchResources()
      this.fetchKpRelations()
    },
    openCreateModal() {
      if (!this.selectedCourseId) {
        this.$message.warning('请先选择课程')
        return
      }
      this.showCreateModal = true
    },
    async fetchResources() {
      if (!this.selectedCourseId) {
        this.resources = []
        this.total = 0
        return
      }
      try {
        const token = localStorage.getItem('jwt_token')
        const body = {
          kcId: this.selectedCourseId,
          page: this.currentPage,
          pageSize: this.pageSize,
          keyword: this.searchKeyword
        }
        if (this.selectedKpId) body.kpId = parseInt(this.selectedKpId, 10)
        const response = await fetch('/api/v1/knowledge/resources/list', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(body)
        })
        const data = await response.json()
        if (data.code === 200) {
          this.resources = data.data?.list || []
          this.total = data.data?.total || 0
          this.totalPages = Math.ceil((data.data?.total || 0) / this.pageSize) || 1
        } else if (data.code === 401) {
          this.$message.error('登录已过期，请重新登录')
        } else {
          this.$message.error(data.message || '获取资源列表失败')
        }
      } catch (error) {
        console.error('Failed to fetch resources:', error)
        this.$message.error('获取资源列表失败')
      }
    },
    
    async fetchKnowledgePoints() {
      if (!this.selectedCourseId) {
        this.knowledgePoints = []
        return
      }
      try {
        const token = localStorage.getItem('jwt_token')
        const response = await fetch('/api/v1/knowledge/points/list', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            kcId: this.selectedCourseId,
            page: 1,
            pageSize: 1000,
            keyword: ''
          })
        })
        const data = await response.json()
        if (data.code === 200) {
          this.knowledgePoints = data.data?.list || []
        } else if (data.code === 401) {
          this.$message.error('登录已过期，请重新登录')
          this.knowledgePoints = []
        } else {
          this.$message.error(data.message || '获取知识点列表失败')
          this.knowledgePoints = []
        }
      } catch (error) {
        console.error('Failed to fetch knowledge points:', error)
        this.$message.error('获取知识点列表失败')
      }
    },
    
    async fetchKpRelations() {
      try {
        const token = localStorage.getItem('jwt_token')
        const response = await fetch('/api/v1/knowledge/points/resources/list', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            page: 1,
            pageSize: 1000
          })
        })
        const data = await response.json()
        if (data.code === 200) {
          this.kpRelations = data.data?.list || []
        } else if (data.code === 401) {
          this.$message.error('登录已过期，请重新登录')
        } else {
          console.error('Failed to fetch relations:', data.message)
          this.kpRelations = []
        }
      } catch (error) {
        console.error('Failed to fetch relations:', error)
        this.kpRelations = []
      }
    },
    
    async handleSubmit() {
      try {
        const token = localStorage.getItem('jwt_token')
        const url = this.showEditModal 
          ? '/api/v1/knowledge/resources/update'
          : '/api/v1/knowledge/resources/create'
        
        const requestBody = this.showEditModal 
          ? { ...this.formData, krId: this.selectedResource.krId }
          : { ...this.formData, kcId: this.selectedCourseId }
        
        const response = await fetch(url, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(requestBody)
        })
        
        const data = await response.json()
        if (data.code === 200) {
          this.$message.success(this.showEditModal ? '资源更新成功' : '资源创建成功')
          this.closeModal()
          this.fetchResources()
        } else if (data.code === 401) {
          this.$message.error('登录已过期，请重新登录')
        } else {
          this.$message.error(data.message || '操作失败')
        }
      } catch (error) {
        console.error('Failed to submit resource:', error)
        this.$message.error('操作失败')
      }
    },
    
    async deleteResource(resource) {
      if (!this.selectedCourseId) return
      if (!confirm('确定要删除这个资源吗？')) return
      
      try {
        const token = localStorage.getItem('jwt_token')
        const response = await fetch('/api/v1/knowledge/resources/delete', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ krId: resource.krId })
        })
        
        const data = await response.json()
        if (data.code === 200) {
          this.$message.success('资源删除成功')
          this.fetchResources()
        } else if (data.code === 401) {
          this.$message.error('登录已过期，请重新登录')
        } else {
          this.$message.error(data.message || '删除失败')
        }
      } catch (error) {
        console.error('Failed to delete resource:', error)
        this.$message.error('删除失败')
      }
    },
    
    async handleBind() {
      try {
        const token = localStorage.getItem('jwt_token')
        
        // 先删除现有的关系
        const existingRelations = this.kpRelations.filter(rel => rel.krId === this.selectedResource.krId)
        for (const relation of existingRelations) {
          await fetch('/api/v1/knowledge/relations/delete', {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({ id: relation.id })
          })
        }
        
        // 创建新的关系
        for (const kp of this.selectedKnowledgePoints) {
          await fetch('/api/v1/knowledge/relations/create', {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              kpId: kp.kpId,
              krId: this.selectedResource.krId
            })
          })
        }
        
        this.$message.success('绑定成功')
        this.showBindModal = false
        this.fetchKpRelations()
        this.fetchUnboundResources()
        if (this.selectedCourseId) {
          this.fetchResources()
        }
      } catch (error) {
        console.error('Failed to bind resource:', error)
        this.$message.error('绑定失败')
      }
    },
    
    viewResource(resource) {
      if (!this.selectedCourseId) return
      let res = Object.assign({}, resource);
      if (res.url && res.url.endsWith('.m3u8')) {
        res.useVedioPlayer = true;
        res.isAudio = res.type === 'audio';
        this.videoOptions = {
          fluid: true,       
          autoplay: false,
          muted: true,
          controls: true,
          preload: 'auto',    
          sources: [{
              src: res.url,
              type: 'application/x-mpegURL'
          }]
        };
      }
      this.selectedResource = res;
      this.showViewModal = true
    },
    
    editResource(resource) {
      if (!this.selectedCourseId) return
      this.selectedResource = resource
      this.formData = {
        name: resource.name || '',
        content: resource.content,
        url: resource.url || '',
        iframe: resource.iframe || '',
        type: resource.type,
        difficulty: resource.difficulty
      }
      this.showEditModal = true
    },
    
    showBindModals(resource) {
      if (!this.selectedCourseId) return
      this.selectedResource = resource
      this.selectedKnowledgePoints = this.getBoundKnowledgePointsList(resource.krId)
      this.showBindModal = true
    },
    
    closeModal() {
      this.showCreateModal = false
      this.showEditModal = false
      this.showViewModal = false
      this.showBindModal = false
      this.selectedResource = null
      this.selectedKnowledgePoints = []
      this.formData = {
        name: '',
        content: '',
        url: '',
        iframe: '',
        type: 'text',
        difficulty: 1
      }
    },
    
    handleSearch() {
      this.currentPage = 1
      this.fetchResources()
    },
    
    handleKpFilter() {
      this.currentPage = 1
      this.fetchResources()
    },
    
    changePage(page) {
      this.currentPage = page
      this.fetchResources()
    },
    handlePageSizeChange(size) {
      this.pageSize = size
      this.currentPage = 1
      this.fetchResources()
    },
    
    filterKnowledgePoints() {
      const keyword = this.kpSearch.toLowerCase()
      this.filteredKnowledgePoints = (this.knowledgePoints || []).filter(kp => 
        kp.name.toLowerCase().includes(keyword) &&
        !this.selectedKnowledgePoints.find(selected => selected.kpId === kp.kpId)
      )
    },
    
    addKnowledgePoint(kp) {
      this.selectedKnowledgePoints.push(kp)
      this.kpSearch = ''
      this.showKpDropdown = false
      this.filterKnowledgePoints()
    },
    
    removeKnowledgePoint(kp) {
      this.selectedKnowledgePoints = this.selectedKnowledgePoints.filter(
        selected => selected.kpId !== kp.kpId
      )
      this.filterKnowledgePoints()
    },
    
    getBoundKnowledgePoints(krId) {
      const relations = this.kpRelations.filter(rel => rel.krId === krId)
      const kpNames = relations.map(rel => {
        const kp = (this.knowledgePoints || []).find(k => k.kpId === rel.kpId)
        return kp ? kp.name : '未知知识点'
      })
      return kpNames.join(', ') || '未绑定'
    },
    
    getBoundKnowledgePointsList(krId) {
      const relations = this.kpRelations.filter(rel => rel.krId === krId)
      return relations.map(rel => {
        const kp = (this.knowledgePoints || []).find(k => k.kpId === rel.kpId)
        return kp || { kpId: rel.kpId, name: '未知知识点' }
      })
    },
    
    truncateContent(content) {
      if (!content || !String(content).trim()) return '-'
      return content.length > 50 ? content.substring(0, 50) + '...' : content
    },
    
    truncateUrl(url) {
      if (url.length > 50) {
        return url.substring(0, 50) + '...'
      }
      return url
    },
    
    truncateIframe(iframe) {
      if (iframe.length > 50) {
        return iframe.substring(0, 50) + '...'
      }
      return iframe
    },
    
    getResourceTypeText(type) {
      const types = {
        audio: '音频',
        lecture: '讲义',
        exercise: '习题',
        text: '文本',
        video: '视频',
        document: '文档',        
      }
      return types[type] || '未知'
    },
    
    getDifficultyText(difficulty) {
      const difficulties = {
        1: '初级',
        2: '中级',
        3: '高级'
      }
      return difficulties[difficulty] || '未知'
    },
    
    formatTime(timestamp) {
      return new Date(timestamp * 1000).toLocaleString()
    }
  }
}
</script>

<style scoped>
.knowledge-resource-admin {
  padding: 20px;
  max-width: 1800px;
  margin: 0 auto;
}

.course-filter-bar {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
  padding: 12px 16px;
  background: #f5f7fa;
  border-radius: 8px;
  border: 1px solid #e4e7ed;
}

.course-filter-bar label {
  font-weight: 500;
  color: #303133;
  white-space: nowrap;
}

.required-mark {
  color: #f56c6c;
}

.selected-course-label {
  margin-right: auto;
  color: #606266;
  font-size: 14px;
}

.course-filter-bar select {
  min-width: 280px;
  padding: 8px 10px;
  border: 1px solid #dcdfe6;
  border-radius: 4px;
}

.course-filter-hint {
  font-size: 13px;
  color: #909399;
}

.legacy-panel {
  margin: 16px 0 24px;
  padding: 16px;
  background: #fffbe6;
  border: 1px solid #ffe58f;
  border-radius: 6px;
}

.legacy-panel__title {
  margin: 0 0 8px;
  font-size: 15px;
  color: #ad6800;
}

.legacy-panel__hint {
  margin: 0 0 12px;
  font-size: 13px;
  color: #8c6d1f;
}

.legacy-panel__warn {
  margin: 0 0 12px;
  font-size: 13px;
  color: #c45656;
}

.legacy-panel__warn a {
  color: #409eff;
  text-decoration: none;
}

.legacy-panel__warn a:hover {
  text-decoration: underline;
}

.legacy-kp-select {
  min-width: 180px;
  padding: 6px 8px;
  border: 1px solid #dcdfe6;
  border-radius: 4px;
}

.btn-assign {
  background-color: #e6a23c;
  color: #fff;
  border: none;
  border-radius: 4px;
  padding: 6px 12px;
  cursor: pointer;
}

.btn-assign:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.empty-course-hint {
  padding: 56px 24px;
  text-align: center;
  color: #909399;
  background: #fafafa;
  border: 1px dashed #dcdfe6;
  border-radius: 8px;
}

.empty-course-hint__title {
  margin: 0 0 8px;
  font-size: 16px;
  font-weight: 500;
  color: #606266;
}

.empty-course-hint__desc {
  margin: 0;
  font-size: 14px;
}

.admin-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  padding-bottom: 15px;
  border-bottom: 2px solid #eee;
}

.admin-header h2 {
  margin: 0;
  color: #333;
}

.btn-primary {
  background: #1976d2;
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  transition: background 0.2s ease;
}

.btn-primary:hover {
  background: #1565c0;
}

.btn-secondary {
  background: #f5f5f5;
  color: #666;
  border: 1px solid #ddd;
  padding: 10px 20px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.2s ease;
}

.btn-secondary:hover {
  background: #e8e8e8;
}

.resource-list {
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  overflow: hidden;
}

.search-bar {
  display: flex;
  gap: 15px;
  padding: 20px;
  border-bottom: 1px solid #eee;
}

.search-bar input,
.search-bar select {
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 14px;
}

.search-bar input {
  flex: 1;
  min-width: 200px;
}

.search-bar select {
  min-width: 150px;
}

.resource-table {
  width: 100%;
  border-collapse: collapse;
}

.resource-table th,
.resource-table td {
  padding: 12px;
  text-align: left;
  border-bottom: 1px solid #eee;
}

.resource-table th {
  background: #f8f9fa;
  font-weight: 600;
  color: #333;
}

.resource-table tr:hover {
  background: #f8f9fa;
}

.btn-view,
.btn-edit,
.btn-bind,
.btn-delete {
  padding: 6px 12px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 12px;
  margin-right: 5px;
  transition: all 0.2s ease;
}

.btn-view {
  background: #28a745;
  color: white;
}

.btn-view:hover {
  background: #218838;
}

.btn-edit {
  background: #ffc107;
  color: #212529;
}

.btn-edit:hover {
  background: #e0a800;
}

.btn-bind {
  background: #17a2b8;
  color: white;
}

.btn-bind:hover {
  background: #138496;
}

.btn-delete {
  background: #dc3545;
  color: white;
}

.btn-delete:hover {
  background: #c82333;
}

.pagination {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 15px;
  padding: 20px;
  border-top: 1px solid #eee;
}

.pagination button {
  padding: 8px 16px;
  border: 1px solid #ddd;
  background: white;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.pagination button:hover:not(:disabled) {
  background: #f8f9fa;
}

.pagination button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.modal {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0,0,0,0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal-content {
  background: white;
  border-radius: 8px;
  padding: 30px;
  max-width: 600px;
  width: 90%;
  max-height: 80vh;
  overflow-y: auto;
}

.modal-content h3 {
  margin: 0 0 20px 0;
  color: #333;
}

.form-group {
  margin-bottom: 20px;
}

.form-group label {
  display: block;
  margin-bottom: 8px;
  font-weight: 500;
  color: #333;
}

.form-group input,
.form-group select,
.form-group textarea {
  width: 100%;
  padding: 10px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 14px;
  box-sizing: border-box;
}

.form-group textarea {
  resize: vertical;
  min-height: 100px;
}

.form-actions {
  display: flex;
  gap: 15px;
  justify-content: flex-end;
  margin-top: 30px;
}

.resource-details {
  margin-bottom: 20px;
}

.detail-item {
  margin-bottom: 15px;
}

.detail-item label {
  font-weight: 600;
  color: #333;
  display: block;
  margin-bottom: 5px;
}

.detail-item span {
  color: #666;
}

.content-display {
  background: #f8f9fa;
  padding: 15px;
  border-radius: 6px;
  border: 1px solid #e9ecef;
  max-height: 200px;
  overflow-y: auto;
}

.resource-url {
  color: #1976d2;
  text-decoration: none;
  word-break: break-all;
}

.resource-url:hover {
  text-decoration: underline;
}

.table-url {
  color: #1976d2;
  text-decoration: none;
  font-size: 12px;
}

.table-url:hover {
  text-decoration: underline;
}

.no-url {
  color: #999;
  font-style: italic;
}

.bound-kps {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.kp-tag {
  background: #e3f2fd;
  color: #1976d2;
  padding: 4px 12px;
  border-radius: 16px;
  font-size: 12px;
  font-weight: 500;
}

.multi-select-container {
  position: relative;
}

.selected-items {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 10px;
  min-height: 40px;
  padding: 8px;
  border: 1px solid #ddd;
  border-radius: 6px;
  background: #f8f9fa;
}

.selected-item {
  background: #e3f2fd;
  color: #1976d2;
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 12px;
  display: flex;
  align-items: center;
  gap: 5px;
}

.remove-btn {
  background: none;
  border: none;
  color: #1976d2;
  cursor: pointer;
  font-size: 14px;
  padding: 0;
  width: 16px;
  height: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.remove-btn:hover {
  color: #d32f2f;
}

.select-dropdown {
  position: relative;
}

.select-dropdown input {
  width: 100%;
  padding: 10px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 14px;
}

.dropdown-list {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: white;
  border: 1px solid #ddd;
  border-top: none;
  border-radius: 0 0 6px 6px;
  max-height: 200px;
  overflow-y: auto;
  z-index: 1000;
}

.dropdown-item {
  padding: 10px;
  cursor: pointer;
  border-bottom: 1px solid #eee;
  transition: background 0.2s ease;
}

.dropdown-item:hover {
  background: #f8f9fa;
}

.dropdown-item:last-child {
  border-bottom: none;
}

.bind-info {
  background: #e3f2fd;
  padding: 15px;
  border-radius: 6px;
  margin-bottom: 20px;
  border-left: 4px solid #1976d2;
}

.bind-info p {
  margin: 0;
  color: #1976d2;
  font-weight: 500;
}

.modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 15px;
  margin-top: 20px;
}

/* Responsive Design */
@media (max-width: 768px) {
  .admin-header {
    flex-direction: column;
    gap: 15px;
    align-items: stretch;
  }
  
  .search-bar {
    flex-direction: column;
  }
  
  .resource-table {
    font-size: 11px;
  }
  
  .resource-table th,
  .resource-table td {
    padding: 6px 4px;
  }
  
  /* 在移动端隐藏一些不太重要的列 */
  .resource-table th:nth-child(4), /* URL */
  .resource-table td:nth-child(4),
  .resource-table th:nth-child(5), /* iframe */
  .resource-table td:nth-child(5),
  .resource-table th:nth-child(8), /* 创建时间 */
  .resource-table td:nth-child(8) {
    display: none;
  }
  
  .btn-view,
  .btn-edit,
  .btn-bind,
  .btn-delete {
    padding: 3px 6px;
    font-size: 10px;
    margin-right: 1px;
  }
  
  .modal-content {
    padding: 15px;
    width: 95%;
  }
  
  .form-actions {
    flex-direction: column;
  }
  
  .selected-items {
    min-height: 60px;
  }
  
  .selected-item {
    font-size: 11px;
    padding: 3px 6px;
  }
  
  /* 移动端iframe预览优化 */
  .iframe-code {
    font-size: 10px;
    padding: 8px;
  }
  
  .iframe-preview-content {
    min-height: 150px;
  }
  
  .iframe-preview-content :deep(iframe) {
    height: 200px;
  }
}

.no-iframe {
  color: #999;
  font-style: italic;
}

.iframe-preview {
  color: #1976d2;
  font-family: monospace;
  font-size: 12px;
  cursor: pointer;
}

.iframe-display {
  background: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 6px;
  padding: 15px;
}

.iframe-code {
  background: #2d3748;
  color: #e2e8f0;
  padding: 10px;
  border-radius: 4px;
  font-family: monospace;
  font-size: 12px;
  margin-bottom: 10px;
  overflow-x: auto;
  white-space: pre-wrap;
  word-break: break-all;
}

.iframe-preview-container {
  border-top: 1px solid #e9ecef;
  padding-top: 10px;
}

.iframe-preview-label {
  font-weight: 600;
  color: #333;
  margin-bottom: 8px;
}

.iframe-preview-content {
  border: 1px solid #ddd;
  border-radius: 4px;
  min-height: 200px;
  background: white;
}

.iframe-preview-content :deep(iframe) {
  width: 100%;
  height: 300px;
  border: none;
  border-radius: 4px;
}
</style> 